import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Icon, IconTile } from 'components/v3';
import { useGetLearningHubTopics, useGetLearningHubVideos } from '../gql';
import {
  VideosSection,
  SectionHeader,
  SectionTitleContainer,
  SectionInfo,
  SectionTitle,
  SectionDescription,
  SeeAllButton,
  VideosGrid,
  WhatsNewGrid,
  WhatsNewContainer,
  WhatsNewHeader,
  WhatsNewIcon,
  WhatsNewInfo,
  WhatsNewTitle,
  NewBadge,
  WhatsNewDescription,
} from '../StyledComponents';
import { topicIconName } from '../topicIcons';
import EmptyState from './EmptyState';
import TopicFiltersContainer from './TopicFiltersContainer';
import VideoCard from './VideoCard';

const DiscoverContent = ({ searchQuery, onPlayVideo, onEmptyChange }) => {
  const [activeTopic, setActiveTopic] = useState(null);

  const { topics, loading: topicsLoading } =
    useGetLearningHubTopics('discover');
  const { videos, loading: videosLoading } = useGetLearningHubVideos({
    mode: 'discover',
    search: searchQuery || undefined,
    topic_slug: activeTopic || undefined,
    limit: 200,
  });

  const loading = topicsLoading || videosLoading;

  // Section has no videos and the user hasn't searched or picked a topic — the
  // search + filter controls have nothing to act on, so signal the parent to
  // hide them and hide the topic filters below.
  const isCompletelyEmpty =
    !loading && videos.length === 0 && !searchQuery && !activeTopic;

  useEffect(() => {
    // Don't update visibility mid-load — hold the prior state until the
    // section settles so the search bar doesn't flash on tab switches.
    if (loading) return;
    if (onEmptyChange) onEmptyChange(isCompletelyEmpty);
  }, [loading, isCompletelyEmpty, onEmptyChange]);

  const newVideos = videos.filter((v) => v.isNew).slice(0, 3);
  const newVideoIds = new Set(newVideos.map((v) => v.id));

  const visibleTopics = topics.filter((t) => t.id !== 'whats-new');

  if (loading && !videos.length) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
    );
  }

  return (
    <div>
      {/* What's New Section */}
      {!activeTopic && newVideos.length > 0 && (
        <WhatsNewContainer>
          <WhatsNewHeader>
            <WhatsNewIcon>
              <Icon name="sparkles" size={26} />
            </WhatsNewIcon>
            <WhatsNewInfo>
              <WhatsNewTitle>
                What's New
                <NewBadge>{newVideos.length} NEW</NewBadge>
              </WhatsNewTitle>
              <WhatsNewDescription>
                Just released features and updates
              </WhatsNewDescription>
            </WhatsNewInfo>
          </WhatsNewHeader>
          <WhatsNewGrid>
            {newVideos.map((video) => (
              <VideoCard key={video.id} video={video} onClick={onPlayVideo} />
            ))}
          </WhatsNewGrid>
        </WhatsNewContainer>
      )}

      {/* Topic Filters */}
      {!isCompletelyEmpty && (
        <TopicFiltersContainer
          topics={visibleTopics}
          activeTopic={activeTopic}
          onSelectTopic={setActiveTopic}
        />
      )}

      {/* Videos by Topic */}
      {videos.length === 0 ? (
        <EmptyState
          searchQuery={searchQuery}
          hasTopicFilter={Boolean(activeTopic)}
          onClearFilter={() => setActiveTopic(null)}
        />
      ) : !activeTopic ? (
        visibleTopics.map((topic) => {
          const topicVideos = videos.filter(
            (v) => v.topic === topic.id && !newVideoIds.has(v.id)
          );
          if (topicVideos.length === 0) return null;

          return (
            <VideosSection key={topic.id}>
              <SectionHeader>
                <SectionTitleContainer>
                  <IconTile
                    icon={topicIconName(topic.icon)}
                    size={40}
                    iconSize={22}
                  />
                  <SectionInfo>
                    <SectionTitle>{topic.name}</SectionTitle>
                    <SectionDescription>{topic.description}</SectionDescription>
                  </SectionInfo>
                </SectionTitleContainer>
                {topicVideos.length > 3 && (
                  <SeeAllButton onClick={() => setActiveTopic(topic.id)}>
                    See all {topicVideos.length}{' '}
                    <Icon name="chevron-right" size={18} />
                  </SeeAllButton>
                )}
              </SectionHeader>
              <VideosGrid>
                {topicVideos.slice(0, 3).map((video) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    onClick={onPlayVideo}
                  />
                ))}
              </VideosGrid>
            </VideosSection>
          );
        })
      ) : (
        <VideosGrid>
          {videos.map((video) => (
            <VideoCard key={video.id} video={video} onClick={onPlayVideo} />
          ))}
        </VideosGrid>
      )}
    </div>
  );
};

DiscoverContent.propTypes = {
  searchQuery: PropTypes.string,
  onPlayVideo: PropTypes.func.isRequired,
  onEmptyChange: PropTypes.func,
};

export default DiscoverContent;
